import { create } from "zustand";
import type { ArtworkIssue } from "../lib/metadata-builder/artwork";
import type { MetadataDay } from "../lib/metadata-history/metadata-targets";
import type { StreamSlot, CommonOptions } from "../lib/schedule-document/schedule-values";
import { systemTimezone } from "../lib/schedule-time/schedule-dates";
import { previewModesFor, type PreviewDayModes } from "../lib/metadata-builder/preview-modes";

export const builderSessionId = "metadata-builder";
export const builderSteps = ["upload", "credit", "dates", "offline", "oneSlot", "twoSlot", "review"] as const;
export type BuilderStep = (typeof builderSteps)[number];
export type BuilderPreviewMode = "mixed" | "one_slot" | "two_slot" | "offline";

type BuilderState = {
  active: boolean;
  step: BuilderStep;
  builderSelection: string | null;
  scheduleSelection: string | null;
  uploading: boolean;
  uploadIssues: ArtworkIssue[];
  previewModes: PreviewDayModes;
  sampleArtistHandle: string;
  sampleOfflineMessage: string | null;
  offlineStarted: boolean;
  oneSlotStarted: boolean;
  twoSlotStarted: boolean;
  sampleSlots: Partial<Record<1 | 2, Partial<StreamSlot>>>;
  sampleCommonOptions: CommonOptions;
  sampleDate: string;
  sampleTimezone: string;
  sampleOfflineActive: boolean;
  setSampleSlot: (number: 1 | 2, patch: Partial<StreamSlot>) => void;
  resetSampleSlot: (number: 1 | 2) => void;
  setSampleCommonOptions: (patch: Partial<CommonOptions>) => void;
  setSampleDate: (date: string) => void;
  setSampleTimezone: (timezone: string) => void;
  setSampleOffline: (patch: { message?: string; active?: boolean }) => void;
  selectedDay: MetadataDay;
  previewDay: MetadataDay | null;
  setSelectedDay: (day: MetadataDay) => void;
  setPreviewDay: (day: MetadataDay | null) => void;
  setSampleArtistHandle: (handle: string) => void;
  setPreviewModes: (modes: PreviewDayModes) => void;
  setStep: (step: BuilderStep) => void;
};

// Navigation and selection are view state. Metadata lives in event history.
export const useMetadataBuilderStore = create<BuilderState>(set => ({
  active: false,
  step: "upload",
  builderSelection: "globals.0",
  scheduleSelection: null,
  uploading: false,
  uploadIssues: [],
  previewModes: previewModesFor("mixed"),
  sampleArtistHandle: "@sample_artist",
  sampleOfflineMessage: null,
  offlineStarted: false,
  oneSlotStarted: false,
  twoSlotStarted: false,
  sampleSlots: {},
  sampleCommonOptions: {},
  sampleDate: "2019-01-28",
  sampleTimezone: systemTimezone,
  sampleOfflineActive: true,
  setSampleSlot: (number, patch) => set(state => ({ sampleSlots: { ...state.sampleSlots, [number]: { ...state.sampleSlots[number], ...patch, active: true } } })),
  resetSampleSlot: number => set(state => ({ sampleSlots: { ...state.sampleSlots, [number]: {} } })),
  setSampleCommonOptions: patch => set(state => ({ sampleCommonOptions: { ...state.sampleCommonOptions, ...patch } })),
  setSampleDate: sampleDate => set({ sampleDate }),
  setSampleTimezone: sampleTimezone => set({ sampleTimezone }),
  setSampleOffline: patch => set(state => ({ sampleOfflineMessage: patch.message ?? state.sampleOfflineMessage, sampleOfflineActive: patch.active ?? state.sampleOfflineActive })),
  selectedDay: "mon",
  previewDay: "mon",
  setSelectedDay: selectedDay => set({ selectedDay, previewDay: selectedDay }),
  setPreviewDay: previewDay => set({ previewDay }),
  setSampleArtistHandle: sampleArtistHandle => set({ sampleArtistHandle }),
  setPreviewModes: previewModes => set({ previewModes }),
  setStep: step => set({ step }),
}));
